/**
 * scenario-start-ux (Phase 4, Task 2.1) stage timer hook。
 *
 * 設計値 (ms):
 *   - title → worldview: 1000
 *   - worldview → closing: 1500
 *
 * `scenarioReady=false` の間は stage="title" で停止。
 * `prefers-reduced-motion: reduce` 検出時は stage="closing" に即遷移しアニメーションを抑制。
 * unmount 時に全 setTimeout を解除 (leak 防止)。
 */

import { useEffect, useState } from "react";
import type {
  StageId,
  UseScenarioStartStageOptions,
  UseScenarioStartStageResult,
} from "./scenario-intro.types";

const DEFAULT_DURATIONS: Record<Exclude<StageId, "title">, number> = {
  worldview: 1000,
  closing: 1500,
};

function readReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useScenarioStartStage(
  options: UseScenarioStartStageOptions,
): UseScenarioStartStageResult {
  const { scenarioReady, stageDurations } = options;
  const reducedMotion = useReducedMotion();
  const initialStage: StageId = reducedMotion ? "closing" : "title";
  const [stage, setStage] = useState<StageId>(initialStage);

  useEffect(() => {
    // reducedMotion の間は stage を "closing" に固定、timer 起動しない
    if (reducedMotion) {
      setStage("closing");
      return;
    }
    // scenario が来るまでは title 固定
    if (!scenarioReady) {
      setStage("title");
      return;
    }

    // scenario ready 確定後、title → worldview → closing を setTimeout で段階進行。
    // 登場人物は開始時に明かさず物語本編で出す。
    const durations = {
      worldview: stageDurations?.worldview ?? DEFAULT_DURATIONS.worldview,
      closing: stageDurations?.closing ?? DEFAULT_DURATIONS.closing,
    };

    // timer のチェイン。unmount / re-run 時に全解除する。
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        setStage("worldview");
        timers.push(
          window.setTimeout(() => {
            setStage("closing");
          }, durations.closing),
        );
      }, durations.worldview),
    );

    return () => {
      for (const id of timers) {
        window.clearTimeout(id);
      }
    };
  }, [
    scenarioReady,
    reducedMotion,
    stageDurations?.worldview,
    stageDurations?.closing,
  ]);

  return { stage, reducedMotion };
}

/**
 * `prefers-reduced-motion: reduce` の現在値を取得する内部 hook。
 * SSR / matchMedia 未対応環境では false を返す。
 */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => readReducedMotion());

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent): void => {
      setReduced(e.matches);
    };
    mq.addEventListener("change", handler);
    return () => {
      mq.removeEventListener("change", handler);
    };
  }, []);

  return reduced;
}
